import type {
  DiagramEdge,
  DiagramNode,
  LinkEvidence,
  MonitorMode,
  MonitorSource,
  NodeStatus,
  ResolvedLinkState,
  ResolvedNodeState,
  TelemetryPayload,
} from './types.js';

export interface StatusEngineInput {
  nowMs: number;
  heartbeatIntervalSec?: number;
  lastHeartbeatAtMs: number | null;
  payload: TelemetryPayload | null;
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

export interface StatusEngineResult {
  siteStatus: NodeStatus;
  timeUnsynced: boolean;
  nodes: ResolvedNodeState[];
  links: ResolvedLinkState[];
}

const STATUS_PRIORITY: NodeStatus[] = [
  'site_unreachable',
  'maintenance',
  'stale',
  'offline',
  'degraded',
  'sleeping',
  'inactive',
  'online',
  'passive',
  'unknown',
];

function higherPriority(a: NodeStatus, b: NodeStatus): NodeStatus {
  return STATUS_PRIORITY.indexOf(a) <= STATUS_PRIORITY.indexOf(b) ? a : b;
}

function isSleepCapable(mode: MonitorMode, type: DiagramNode['type']): boolean {
  if (mode === 'dhcp_presence' || mode === 'arp_presence' || mode === 'wifi_registration') {
    return true;
  }
  return type === 'laptop' || type === 'tablet' || type === 'display' || type === 'wifi_phone';
}

function parseTimeMs(value?: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function detectTimeUnsynced(
  routerTime: string,
  receivedAtMs: number,
  skewSec = 300,
): boolean {
  const routerMs = parseTimeMs(routerTime);
  if (routerMs === null) return true;
  return Math.abs(receivedAtMs - routerMs) > skewSec * 1000;
}

export function detectReboot(prevUptime: number | null, nextUptime: number): boolean {
  if (prevUptime === null) return false;
  return nextUptime < prevUptime;
}

export function isDuplicateSequence(prev: number | null, next: number): boolean {
  return prev !== null && next === prev;
}

export function resolveSiteStatus(
  nowMs: number,
  lastHeartbeatAtMs: number | null,
  heartbeatIntervalSec = 60,
): NodeStatus {
  if (lastHeartbeatAtMs === null) return 'unknown';
  const miss = (nowMs - lastHeartbeatAtMs) / 1000;
  if (miss > heartbeatIntervalSec * 3) return 'site_unreachable';
  if (miss > heartbeatIntervalSec * 2.5) return 'stale';
  return 'online';
}

function mapTargetStatus(
  raw: TelemetryPayload['targets'][number]['status'],
  node: DiagramNode,
): NodeStatus {
  if (raw === 'offline' && node.criticality !== 'critical' && isSleepCapable(node.monitorMode, node.type)) {
    return node.expectedPresence === 'always' ? 'inactive' : 'sleeping';
  }
  if (raw === 'inactive') return 'inactive';
  if (raw === 'sleeping') return 'sleeping';
  if (raw === 'degraded') return 'degraded';
  if (raw === 'online') return 'online';
  if (raw === 'offline') return 'offline';
  return 'unknown';
}

function evidenceForMode(mode: MonitorMode): string {
  switch (mode) {
    case 'passive':
      return 'passive';
    case 'router_interface':
      return 'interface';
    case 'neighbor':
      return 'neighbor';
    case 'dhcp_presence':
    case 'arp_presence':
      return 'presence';
    case 'wifi_registration':
      return 'wifi';
    case 'icmp':
    case 'tcp':
    case 'http':
      return 'netwatch';
    case 'derived':
      return 'derived';
    case 'manual':
      return 'manual';
    default:
      return 'unknown';
  }
}

function linkEvidence(source: MonitorSource): LinkEvidence {
  if (source === 'interface' || source === 'neighbor' || source === 'manual') return 'measured';
  if (source === 'endpoint-derived') return 'derived';
  return 'unknown';
}

function deriveLinkStatus(
  edge: DiagramEdge,
  nodeStates: Map<string, ResolvedNodeState>,
): ResolvedLinkState {
  const evidence = linkEvidence(edge.monitorSource);
  if (evidence === 'unknown') {
    return {
      edgeId: edge.id,
      status: 'unknown',
      evidence,
      monitorSource: edge.monitorSource,
    };
  }

  if (evidence === 'measured') {
    // Measured links need telemetry binding; without payload key stay unknown (not green).
    return {
      edgeId: edge.id,
      status: 'unknown',
      evidence: edge.monitorKey ? 'measured' : 'unknown',
      monitorSource: edge.monitorSource,
    };
  }

  const source = nodeStates.get(edge.sourceNodeId);
  const target = nodeStates.get(edge.targetNodeId);
  if (!source || !target) {
    return {
      edgeId: edge.id,
      status: 'unknown',
      evidence: 'derived',
      monitorSource: edge.monitorSource,
    };
  }

  let status: NodeStatus = 'online';
  if (source.status === 'offline' || target.status === 'offline') status = 'offline';
  else if (source.status === 'degraded' || target.status === 'degraded') status = 'degraded';
  else if (
    source.status === 'sleeping' ||
    target.status === 'sleeping' ||
    source.status === 'inactive' ||
    target.status === 'inactive'
  ) {
    status = 'inactive';
  } else if (source.status === 'unknown' || target.status === 'unknown') {
    status = 'unknown';
  }

  return {
    edgeId: edge.id,
    status,
    evidence: 'derived',
    monitorSource: edge.monitorSource,
  };
}

export function resolveStatuses(input: StatusEngineInput): StatusEngineResult {
  const interval = input.heartbeatIntervalSec ?? 60;
  const siteStatus = resolveSiteStatus(input.nowMs, input.lastHeartbeatAtMs, interval);
  const timeUnsynced = input.payload
    ? detectTimeUnsynced(input.payload.routerTime, input.lastHeartbeatAtMs ?? input.nowMs)
    : false;

  const targetByKey = new Map(
    (input.payload?.targets ?? []).map((t) => [t.monitorKey, t] as const),
  );
  const ifaceByKey = new Map(
    (input.payload?.interfaces ?? []).map((i) => [i.monitorKey, i] as const),
  );
  const wanByKey = new Map((input.payload?.wan.links ?? []).map((w) => [w.monitorKey, w] as const));

  const nodes: ResolvedNodeState[] = input.nodes.map((node) => {
    if (siteStatus === 'site_unreachable') {
      return {
        monitorKey: node.monitorKey || node.id,
        status: 'site_unreachable',
        evidence: 'heartbeat',
      };
    }

    if (node.maintenanceUntil && parseTimeMs(node.maintenanceUntil)! > input.nowMs) {
      return {
        monitorKey: node.monitorKey || node.id,
        status: 'maintenance',
        evidence: 'manual',
        updatedAt: node.maintenanceUntil,
      };
    }

    if (node.monitorMode === 'passive' || node.type === 'zone') {
      return {
        monitorKey: node.monitorKey || node.id,
        status: node.type === 'zone' ? 'passive' : 'passive',
        evidence: 'passive',
      };
    }

    const key = node.monitorKey;
    const target = key ? targetByKey.get(key) : undefined;
    const iface = key ? ifaceByKey.get(key) : undefined;
    const wan = key ? wanByKey.get(key) : undefined;

    let status: NodeStatus = 'unknown';
    let evidence = evidenceForMode(node.monitorMode);
    let updatedAt: string | undefined;

    if (iface) {
      evidence = 'interface';
      status = iface.disabled ? 'offline' : iface.running ? 'online' : 'offline';
      if ((iface.rxErrors ?? 0) + (iface.txErrors ?? 0) > 0 && status === 'online') {
        status = 'degraded';
      }
    } else if (wan) {
      evidence = 'interface';
      status = wan.status === 'online' ? 'online' : wan.status === 'standby' ? 'inactive' : 'offline';
    } else if (target) {
      status = mapTargetStatus(target.status, node);
      evidence = 'netwatch';
      updatedAt = target.changedAt;
      const changedMs = parseTimeMs(target.changedAt);
      if (changedMs !== null) {
        const ageSec = (input.nowMs - changedMs) / 1000;
        const nodeInterval = node.criticality === 'critical' ? 30 : 90;
        if (ageSec > nodeInterval * 2.5) {
          status = higherPriority('stale', status);
          evidence = 'stale';
        }
      }
    } else if (node.monitorMode === 'manual') {
      status = 'unknown';
      evidence = 'manual';
    }

    if (siteStatus === 'stale') {
      status = higherPriority('stale', status);
    }

    return {
      monitorKey: key || node.id,
      status,
      evidence,
      updatedAt,
    };
  });

  const nodeStateById = new Map<string, ResolvedNodeState>();
  input.nodes.forEach((n, idx) => nodeStateById.set(n.id, nodes[idx]!));

  // Apply measured interface/netwatch keys onto edges when monitorKey matches
  const links: ResolvedLinkState[] = input.edges.map((edge): ResolvedLinkState => {
    const base = deriveLinkStatus(edge, nodeStateById);
    if (edge.monitorKey && input.payload) {
      const iface = ifaceByKey.get(edge.monitorKey);
      const target = targetByKey.get(edge.monitorKey);
      const wan = wanByKey.get(edge.monitorKey);
      if (iface) {
        let status: NodeStatus = iface.disabled
          ? 'offline'
          : iface.running
            ? (iface.rxErrors ?? 0) + (iface.txErrors ?? 0) > 0
              ? 'degraded'
              : 'online'
            : 'offline';
        if (siteStatus === 'site_unreachable') status = 'site_unreachable';
        return {
          edgeId: edge.id,
          status,
          evidence: 'measured',
          monitorSource: edge.monitorSource,
        };
      }
      if (wan) {
        let status: NodeStatus =
          wan.status === 'online'
            ? 'online'
            : wan.status === 'standby'
              ? 'inactive'
              : 'offline';
        if (siteStatus === 'site_unreachable') status = 'site_unreachable';
        return {
          edgeId: edge.id,
          status,
          evidence: 'measured',
          monitorSource: edge.monitorSource,
        };
      }
      if (target) {
        const probeNode: DiagramNode = {
          id: edge.id,
          monitorKey: edge.monitorKey,
          type: 'generic',
          label: edge.label ?? edge.monitorKey,
          position: { x: 0, y: 0 },
          criticality: edge.criticality,
          monitorMode: 'icmp',
          expectedPresence: 'always',
        };
        let status: NodeStatus = mapTargetStatus(target.status, probeNode);
        if (siteStatus === 'site_unreachable') status = 'site_unreachable';
        return {
          edgeId: edge.id,
          status,
          evidence: 'measured',
          monitorSource: edge.monitorSource,
        };
      }
    }
    if (siteStatus === 'site_unreachable') {
      return { ...base, status: 'site_unreachable' };
    }
    return base;
  });

  return { siteStatus, timeUnsynced, nodes, links };
}
